import React, { useState, useMemo } from 'react';
import styles from './FramedArtwork.module.css';

/**
 * FramedArtwork
 *
 * A self-contained, CSS-only artwork framing preview inspired by the
 * Electric Gallery shop experience (https://electricgallery.co.uk). The
 * frame and mat are rendered purely with CSS variables driven from React
 * state so there is no canvas, no images of frames — just one CSS box.
 *
 * Props:
 *   imageSrc      string                       URL of the artwork image
 *   imageAlt      string                       Alt text for the image
 *   onAddToCart   (frameColour: string|null, frameLabel: string|null) => void
 *                                              Called with the colour hex (e.g. '#2e2e2e')
 *                                              + the human label (e.g. 'Black'); both null
 *                                              for "No Frame".
 *   className     string (optional)            Extra class for the root.
 *   defaultFrame  string|null (optional)       Initial selection (default: null = "No Frame").
 *   frameWidth    string (optional)            CSS length for the frame thickness.
 *                                              Default '12px'.
 *   matWidth      string (optional)            CSS length for the white mount.
 *                                              Default '20px'.
 *
 * Behaviour:
 *   • Default selection is "No Frame" so visitors see the raw artwork first.
 *   • Selecting any colour swatch re-renders the wrapper with frame+mat.
 *   • Swatches show a clear selected ring; whole control is keyboard-accessible.
 *   • All styles are scoped via CSS Modules — nothing leaks out.
 *   • Works on mobile (uses clamp() + max-width: 100%).
 */

// Order + labels + hex match the request brief (Black/White/Oak/Gold/Silver).
const FRAME_OPTIONS = [
  { key: 'none',   label: 'No Frame', colour: null,       isNoFrame: true },
  { key: 'black',  label: 'Black',    colour: '#2e2e2e' },
  { key: 'white',  label: 'White',    colour: '#fafafa' },
  { key: 'oak',    label: 'Oak',      colour: '#b88a4a' },
  { key: 'gold',   label: 'Gold',     colour: '#c9a45e' },
  { key: 'silver', label: 'Silver',   colour: '#bcbcbf' },
];

const FramedArtwork = ({
  imageSrc,
  imageAlt = '',
  onAddToCart,
  className = '',
  defaultFrame = null,
  frameWidth = '12px',
  matWidth = '20px',
  ctaLabel = 'Add to Cart',
}) => {
  const [selectedColour, setSelectedColour] = useState(defaultFrame);

  // The active option is the one whose hex matches state, falling back to "No Frame".
  const activeKey = useMemo(() => {
    if (!selectedColour) return 'none';
    const hit = FRAME_OPTIONS.find((f) => f.colour === selectedColour);
    return hit ? hit.key : 'none';
  }, [selectedColour]);

  // Inline-style object drives the three CSS custom properties used by the wrapper.
  // Setting frame-width / mat-width to 0 collapses the wrapper back to a bare <img>.
  const framePreviewStyle = selectedColour
    ? {
        '--frame-color': selectedColour,
        '--frame-width': frameWidth,
        '--mat-width': matWidth,
      }
    : {
        '--frame-color': 'transparent',
        '--frame-width': '0px',
        '--mat-width': '0px',
      };

  const handleSwatch = (option) => {
    setSelectedColour(option.colour);
  };

  const handleAdd = () => {
    if (typeof onAddToCart === 'function') {
      const opt = FRAME_OPTIONS.find((f) => f.key === activeKey);
      onAddToCart(selectedColour, opt ? opt.label : null);
    }
  };

  const activeOption = FRAME_OPTIONS.find((f) => f.key === activeKey) || FRAME_OPTIONS[0];

  return (
    <div className={`${styles.framedArtwork} ${className}`} data-testid="framed-artwork">
      {/* Live preview --------------------------------------------------- */}
      <div className={styles.previewWrap}>
        <div
          className={`${styles.previewFrame} ${!selectedColour ? styles.noFrame : ''}`}
          style={framePreviewStyle}
          data-testid="framed-artwork-preview"
        >
          <img src={imageSrc} alt={imageAlt} loading="lazy" />
        </div>
      </div>

      {/* Swatch picker -------------------------------------------------- */}
      <div
        className={styles.swatchRow}
        role="radiogroup"
        aria-label="Choose a frame colour"
      >
        {FRAME_OPTIONS.map((opt) => {
          const isSelected = opt.key === activeKey;
          return (
            <button
              key={opt.key}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={opt.label}
              onClick={() => handleSwatch(opt)}
              className={`${styles.swatchButton} ${isSelected ? styles.selected : ''}`}
              data-testid={`framed-artwork-swatch-${opt.key}`}
            >
              <span
                className={`${styles.swatchDot} ${opt.isNoFrame ? styles.noFrameDot : ''}`}
                style={opt.colour ? { background: opt.colour } : undefined}
                aria-hidden="true"
              />
              <span className={styles.swatchLabel}>{opt.label}</span>
            </button>
          );
        })}
      </div>

      {/* Add to cart ---------------------------------------------------- */}
      <div className={styles.summary} data-testid="framed-artwork-summary">
        Frame: <strong>{activeOption.label}</strong>
      </div>

      <button
        type="button"
        className={styles.addToCart}
        onClick={handleAdd}
        data-testid="framed-artwork-add-to-cart"
      >
        {ctaLabel}
      </button>
    </div>
  );
};

export default FramedArtwork;
